import { useEffect, useRef, useState } from "react";
import { Crop, Loader2, Search, Trash2, Upload } from "lucide-react";
import { CoverCropDialog } from "@/components/CoverCropDialog";
import { CroppedCoverImage } from "@/components/CroppedCoverImage";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

export const coverUrl = (cover, legacyPath) => cover ? (cover.source === "upload" ? fileUrl(cover.file_ref) : cover.url) : fileUrl(legacyPath);

export const CoverCredit = ({ cover, prefix, overlay = false }) => cover?.source === "unsplash" ? (
  <p data-testid={`${prefix}-credit`} className={`text-[10px] break-words ${overlay ? "leading-tight text-white" : "leading-relaxed text-stone-500"}`}>
    Photo by <a data-testid={`${prefix}-photographer`} href={cover.photographer_url} target="_blank" rel="noopener noreferrer" className={overlay ? "underline hover:text-white/80 transition-colors" : "underline hover:text-stone-900 transition-colors"}>{cover.photographer_name}</a> on <a data-testid={`${prefix}-unsplash`} href={cover.unsplash_url} target="_blank" rel="noopener noreferrer" className={overlay ? "underline hover:text-white/80 transition-colors" : "underline hover:text-stone-900 transition-colors"}>Unsplash</a>
  </p>
) : null;

export const CoverCreditOverlay = ({ cover, prefix }) => cover?.source === "unsplash" ? (
  <div data-testid={`${prefix}-credit-overlay`} className="absolute bottom-2 left-2 z-20 max-w-[calc(100%-1rem)] rounded-md bg-black/55 px-2 py-1 backdrop-blur-[2px]">
    <CoverCredit cover={cover} prefix={prefix} overlay />
  </div>
) : null;

const CoverThumbnail = ({ cover, crop, testid, size = 56 }) => {
  const aspect = crop?.aspect || (cover.width && cover.height ? cover.width / cover.height : 1);
  return <span className="shrink-0 grid place-items-center overflow-hidden rounded-xl bg-stone-100" style={{ width: size, height: size }}><span style={{ width: Math.min(size, size * aspect) }}><CroppedCoverImage src={coverUrl(cover)} crop={crop} alt="Selected cover" testid={testid} width={cover.width} height={cover.height} /></span></span>;
};

const errorMessage = (error) => typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Could not complete this step. Please try again.";
const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export const BucketCoverField = ({ title, current, currentCrop, onChoose, onBusyChange, disabled, editingWithCover = false }) => {
  const [changeCoverOpen, setChangeCoverOpen] = useState(false);
  const showCoverOptions = !editingWithCover || !current || changeCoverOpen;
  const [mode, setMode] = useState(null);
  const [searchHint, setSearchHint] = useState("");
  const [searchedQuery, setSearchedQuery] = useState("");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState("");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [lastFile, setLastFile] = useState(null);
  const requestRef = useRef(null);
  const uploadRef = useRef(null);
  const fileRef = useRef(null);
  const locked = busy && stage !== "Searching photos…";

  useEffect(() => { onBusyChange(locked); }, [locked, onBusyChange]);
  useEffect(() => () => { onBusyChange(false); }, [onBusyChange]);
  useEffect(() => () => {
    requestRef.current?.abort();
    if (uploadRef.current) api.delete(`/covers/uploads/${uploadRef.current}`).catch(() => {});
  }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const begin = (message) => {
    requestRef.current?.abort();
    requestRef.current = new AbortController();
    setBusy(true); setStage(message); setError("");
    return requestRef.current.signal;
  };

  const search = async (nextPage = 1) => {
    if (busy || disabled) return;
    setMode("search"); setError("");
    const term = nextPage === 1 ? (title || "").trim().slice(0, 100) : searchedQuery;
    if (term.length < 2) {
      setResults([]); setPage(0); setHasMore(false); setSearchedQuery("");
      setSearchHint(term ? "Enter at least two characters in Idea / Destination below, then click Search Unsplash." : "Fill in Idea / Destination below, then click Search Unsplash.");
      return;
    }
    setSearchHint("");
    const signal = begin("Searching photos…");
    try {
      const { data } = await api.get("/covers/unsplash/search", { params: { q: term, page: nextPage }, signal });
      if (signal.aborted) return;
      setResults((old) => [...new Map([...(nextPage === 1 ? [] : old), ...data.results].map((photo) => [photo.photo_id, photo])).values()]);
      setSearchedQuery(term); setPage(nextPage); setHasMore(data.has_more);
    } catch (err) { if (!signal.aborted) setError(errorMessage(err)); }
    finally { if (!signal.aborted) { setBusy(false); setStage(""); } }
  };

  const selectPhoto = async (photo) => {
    const signal = begin("Selecting photo…");
    try {
      const { data } = await api.post("/covers/unsplash", { photo_id: photo.photo_id, selection_id: crypto.randomUUID() }, { signal });
      setCandidate({ image: data, crop: null });
    } catch (err) { if (!signal.aborted) setError(errorMessage(err)); }
    finally { if (!signal.aborted) { setBusy(false); setStage(""); } }
  };

  const uploadPhoto = async (file) => {
    if (!file || busy) return;
    if (!allowedTypes.includes(file.type) || file.size > 20 * 1024 * 1024 || !file.size) {
      setError("Choose a JPG, PNG, WebP or GIF photo up to 20 MB."); return;
    }
    setLastFile(file); setPreview(URL.createObjectURL(file)); setProgress(0); setUploading(true);
    const signal = begin("Starting upload…");
    let sessionId = null;
    try {
      const { data: session } = await api.post("/covers/uploads", { filename: file.name, size: file.size, content_type: file.type }, { signal });
      sessionId = session.upload_id;
      uploadRef.current = sessionId;
      const count = Math.ceil(file.size / session.chunk_size);
      for (let index = 0; index < count; index++) {
        const start = index * session.chunk_size;
        setStage(`Uploading photo · part ${index + 1} of ${count}`);
        await api.put(`/covers/uploads/${session.upload_id}/parts/${index}`, file.slice(start, start + session.chunk_size), {
          signal, headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (event) => { if (!signal.aborted) setProgress(Math.min(95, Math.round((start + event.loaded) / file.size * 95))); },
        });
      }
      setProgress(95); setStage("Checking and storing photo…");
      const { data } = await api.post(`/covers/uploads/${session.upload_id}/complete`, {}, { signal });
      uploadRef.current = null;
      if (signal.aborted) return;
      setUploading(false); setProgress(100);
      setCandidate({ image: data, crop: null });
    } catch (err) {
      if (!signal.aborted) setError(errorMessage(err));
      if (sessionId) {
        api.delete(`/covers/uploads/${sessionId}`).catch(() => {});
        if (uploadRef.current === sessionId) uploadRef.current = null;
      }
    } finally {
      if (requestRef.current?.signal === signal) { setBusy(false); setUploading(false); setStage(""); }
    }
  };

  const cancelUpload = () => {
    requestRef.current?.abort();
    if (uploadRef.current) { api.delete(`/covers/uploads/${uploadRef.current}`).catch(() => {}); uploadRef.current = null; }
    setBusy(false); setUploading(false); setStage(""); setProgress(0);
  };

  const applyCrop = async (crop) => {
    const signal = begin("Saving cover…");
    try {
      await onChoose(candidate.image, crop);
      setCandidate(null);
      requestAnimationFrame(() => document.querySelector('[data-testid="cover-current-crop"]')?.focus());
    } finally { if (!signal.aborted) { setBusy(false); setStage(""); } }
  };

  const cancelCrop = () => {
    setCandidate(null);
    requestAnimationFrame(() => document.querySelector(`[data-testid="${mode === "upload" ? "cover-upload-choose" : current ? "cover-current-crop" : "cover-mode-search"}"]`)?.focus());
  };

  const removeCover = async () => {
    setConfirmRemove(false);
    const signal = begin("Removing cover…");
    try { await onChoose(null, null); setChangeCoverOpen(true); }
    catch (err) { if (!signal.aborted) setError(errorMessage(err)); }
    finally { if (!signal.aborted) { setBusy(false); setStage(""); } }
  };

  const currentCover = current && <div data-testid="cover-current" className="flex items-center gap-2 rounded-2xl border border-stone-200 p-2">
    {editingWithCover ? <button type="button" data-testid="cover-current-change" aria-label="Change current cover" aria-expanded={changeCoverOpen} disabled={busy || disabled} onClick={() => setChangeCoverOpen(true)} className="shrink-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:opacity-80 transition-opacity disabled:opacity-50">
      <CoverThumbnail cover={current} crop={currentCrop} testid="cover-current-preview" size={48} />
    </button> : <CoverThumbnail cover={current} crop={currentCrop} testid="cover-current-preview" size={48} />}
    <div className="flex-1 min-w-0"><p data-testid="cover-current-label" className="text-xs font-bold text-stone-600">Current cover</p><CoverCredit cover={current} prefix="cover-current" /></div>
    <Button type="button" data-testid="cover-current-crop" aria-label="Crop current cover" title="Crop cover" size="icon" variant="ghost" disabled={busy || disabled} onClick={() => setCandidate({ image: current, crop: currentCrop || null })} className="shrink-0 rounded-full text-stone-500"><Crop className="w-4 h-4" /></Button>
    <Button type="button" data-testid="cover-remove" aria-label="Remove cover image" title="Remove cover" size="icon" variant="ghost" disabled={busy || disabled} onClick={() => setConfirmRemove(true)} className="shrink-0 rounded-full text-stone-500"><Trash2 className="w-4 h-4" /></Button>
  </div>;

  return (
    <section data-testid="bucket-cover-section" aria-label="Cover image" className="min-w-0 space-y-3">
      {editingWithCover && currentCover}
      {showCoverOptions && <>
        <p id="bucket-cover-label" data-testid="bucket-cover-label" className="text-sm font-bold text-stone-700">{editingWithCover && current ? "Change current cover" : <>Add cover image <span className="font-normal text-stone-400">(optional)</span></>}</p>
        <div data-testid="cover-source-tabs" role="group" aria-label="Cover image source" className="flex gap-2">
          <Button type="button" data-testid="cover-mode-search" aria-pressed={mode === "search"} disabled={busy || disabled} onClick={() => search()} variant="outline" className={`flex-1 min-w-0 rounded-full px-2 text-xs sm:text-sm ${mode === "search" ? "bg-ac-soft text-ac-softfg border-ac" : ""}`}><Search aria-hidden="true" className="w-4 h-4 mr-1.5 shrink-0" />Search Unsplash</Button>
          <Button type="button" data-testid="cover-mode-upload" aria-pressed={mode === "upload"} disabled={busy || disabled} onClick={() => { setMode("upload"); setError(""); }} variant="outline" className={`flex-1 min-w-0 rounded-full px-2 text-xs sm:text-sm ${mode === "upload" ? "bg-ac-soft text-ac-softfg border-ac" : ""}`}><Upload aria-hidden="true" className="w-4 h-4 mr-1.5 shrink-0" />Upload photo</Button>
        </div>
      </>}
      {!editingWithCover && currentCover}
      {mode === "search" && <>
        {!!results.length && <div data-testid="cover-search-results" className="grid grid-cols-2 sm:grid-cols-3 auto-rows-max items-start gap-3 max-h-52 overflow-y-auto overscroll-contain pr-1">
            {results.map((photo) => <div key={photo.photo_id} className="min-w-0 overflow-hidden rounded-xl border border-stone-200 bg-white">
              <button type="button" data-testid={`cover-photo-${photo.photo_id}`} aria-label={`Use photo by ${photo.photographer_name}`} disabled={busy || disabled} onClick={() => selectPhoto(photo)} className="block w-full overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ac">
                <img src={photo.thumbnail_url} alt={photo.alt} width={photo.width} height={photo.height} loading="lazy" className="aspect-[4/3] w-full object-cover hover:scale-[1.03] transition-transform duration-200" />
              </button>
              <div className="p-2"><CoverCredit cover={{ ...photo, source: "unsplash" }} prefix={`cover-result-${photo.photo_id}`} /></div>
            </div>)}
          </div>}
        {!busy && page > 0 && !results.length && !error && <p data-testid="cover-search-empty" role="status" className="py-2 text-sm text-stone-500">No photos found. Try another name or upload your own photo.</p>}
        {searchHint && <p data-testid="cover-search-hint" role="status" className="py-2 text-sm text-stone-500">{searchHint}</p>}
        {hasMore && <Button type="button" data-testid="cover-search-more" variant="ghost" disabled={busy || disabled} onClick={() => search(page + 1)} className="rounded-full text-sm">More photos</Button>}
      </>}
      {mode === "upload" && <div data-testid="cover-upload-area" className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-4 text-center">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" data-testid="cover-upload-input" aria-label="Choose cover photo" className="hidden" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; uploadPhoto(file); }} />
        {preview && <img data-testid="cover-upload-preview" src={preview} alt="Upload preview" className="mx-auto mb-4 max-h-28 rounded-xl object-contain" />}
        <Button type="button" data-testid="cover-upload-choose" disabled={busy || disabled} variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full"><Upload className="w-4 h-4 mr-2" />Choose photo</Button>
        <p data-testid="cover-upload-help" className="mt-3 text-xs text-stone-500">JPG, PNG, WebP or GIF · up to 20 MB</p>
        {lastFile && error && <Button type="button" data-testid="cover-upload-retry" disabled={busy || disabled} variant="ghost" onClick={() => uploadPhoto(lastFile)} className="mt-2 rounded-full">Retry upload</Button>}
      </div>}
      {busy && <div data-testid="cover-progress" role="status" aria-live="polite" className="space-y-2 text-sm text-stone-600">
        <p className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{stage}{uploading ? ` · ${progress}%` : ""}</p>
        {uploading && <progress data-testid="cover-upload-progress" aria-label="Photo upload progress" max="100" value={progress} className="h-2 w-full accent-[var(--ac)]" />}
      </div>}
      {error && <p data-testid="cover-picker-error" role="alert" className="text-sm text-rose-600 break-words">{error}</p>}
      {uploading && <Button type="button" data-testid="cover-upload-cancel" variant="outline" onClick={cancelUpload} className="rounded-full">Cancel upload</Button>}
      {candidate && <CoverCropDialog key={candidate.image.id || candidate.image.file_ref} src={coverUrl(candidate.image)} initialCrop={candidate.crop} credit={<CoverCredit cover={candidate.image} prefix="cover-crop" />} onApply={applyCrop} onCancel={cancelCrop} />}
      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent data-testid="cover-remove-dialog" className="w-[calc(100%-2rem)] max-w-sm rounded-3xl">
          <AlertDialogHeader><AlertDialogTitle data-testid="cover-remove-title">Remove this cover?</AlertDialogTitle><AlertDialogDescription data-testid="cover-remove-description">The destination stays saved, with a clean background instead of a photo.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="cover-remove-cancel" className="rounded-full">Keep cover</AlertDialogCancel><AlertDialogAction data-testid="cover-remove-confirm" onClick={removeCover} className="rounded-full bg-ac hover:bg-ac-hover">Remove cover</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
